import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { useFirebase } from '../firebase/FirebaseContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';

const FineTuning = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { db } = useFirebase();
    const [voiceModels, setVoiceModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        dataSource: 'zip', // 'zip' or 'huggingface'
        zipFile: null,
        huggingfaceLink: '',
        trainingConfig: {
            audioSampleRate: 16000,
            audioDuration: 10,
            numberOfAudioFiles: 100
        }
    });

    useEffect(() => {
        const fetchVoiceModels = async () => {
            if (!user) {
                console.log('No user found, skipping fetch');
                return;
            }

            try {
                console.log('Fetching voice models for user:', user.uid);
                // Query voice models for the current user
                const q = query(
                    collection(db, 'voice_models'),
                    where('userId', '==', user.uid)
                );

                // Get the documents
                const snapshot = await getDocs(q);
                console.log('Raw snapshot:', snapshot);
                console.log('Number of docs:', snapshot.docs.length);
                
                const models = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        console.log('Document data:', data);
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate()
                        };
                    })
                    .filter(model => !model.isDeleted); // Filter out deleted models in memory

                // Sort the models in memory
                models.sort((a, b) => b.createdAt - a.createdAt);
                console.log('Processed models:', models);
                setVoiceModels(models);
            } catch (error) {
                console.error('Error fetching voice models:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchVoiceModels();
    }, [user, db]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'training':
                return 'bg-blue-100 text-blue-800';
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-sm p-8">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-2xl font-medium text-gray-900 mb-2">
                                Custom Voice Fine-Tuning
                            </h3>
                            <p className="text-gray-500">Manage and create custom voice models</p>
                        </div>
                        <button
                            onClick={() => navigate('/fine-tuning/create')}
                            className="px-6 py-3 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors duration-200"
                        >
                            Create New Voice Model
                        </button>
                    </div>

                    <div className="mb-8 bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
                        Train custom voice models with your audio samples. The process requires clean audio files with clear speech.
                    </div>

                    <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-4">
                            Your Voice Models
                        </h4>
                        {loading ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                                <p className="mt-2 text-gray-500">Loading voice models...</p>
                            </div>
                        ) : voiceModels.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                                <p className="text-gray-500">You haven't created any voice models yet.</p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {voiceModels.map((model) => (
                                            <tr key={model.id}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{model.name}</div>
                                                    <div className="text-sm text-gray-500">{model.description}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {model.createdAt?.toLocaleDateString()}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(model.status)}`}>
                                                        {model.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button
                                                        onClick={() => navigate(`/fine-tuning/${model.id}`)}
                                                        className="text-gray-600 hover:text-gray-900"
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FineTuning;